
import { supabase, invokeFunction } from "@/integrations/supabase/client";

export interface RetryableChunk {
  export_id: string;
  chunk_index: number;
  retry_count: number;
  last_error: string;
  can_retry: boolean;
}

/**
 * Finds chunks that can be retried
 */
export async function findRetryableChunks(exportId?: string): Promise<RetryableChunk[]> {
  let query = supabase
    .from('csv_export_chunks')
    .select('export_id, chunk_index, retry_count, error_message')
    .eq('status', 'failed')
    .lt('retry_count', 3);

  if (exportId) {
    query = query.eq('export_id', exportId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error finding retryable chunks:', error);
    throw error;
  }

  return (data || []).map(chunk => ({
    export_id: chunk.export_id,
    chunk_index: chunk.chunk_index,
    retry_count: chunk.retry_count,
    last_error: chunk.error_message || '',
    can_retry: chunk.retry_count < 3
  }));
}

/**
 * Triggers retry for failed chunks
 */
export async function retryFailedChunks(exportId?: string): Promise<void> {
  const { error } = await invokeFunction(supabase, 'background-csv-export-coordinator', {
    body: {
      retryFailedChunks: true,
      exportId
    }
  });

  if (error) {
    console.error('Error triggering retry:', error);
    throw error;
  }
}

/**
 * Gets retry statistics for an export
 */
export async function getRetryStats(exportId: string) {
  const { data: chunks, error } = await supabase
    .from('csv_export_chunks')
    .select('status, retry_count, error_message')
    .eq('export_id', exportId);

  if (error) {
    console.error('Error getting retry stats:', error);
    throw error;
  }

  const stats = {
    total: chunks.length,
    completed: chunks.filter(c => c.status === 'completed').length,
    failed: chunks.filter(c => c.status === 'failed').length,
    permanently_failed: chunks.filter(c => c.status === 'failed' && c.retry_count >= 3).length,
    processing: chunks.filter(c => c.status === 'processing').length,
    pending: chunks.filter(c => c.status === 'pending').length,
    total_retries: chunks.reduce((sum, c) => sum + (c.retry_count || 0), 0),
    max_retries: Math.max(...chunks.map(c => c.retry_count || 0)),
    error_categories: chunks
      .filter(c => c.error_message)
      .reduce((acc, c) => {
        const category = c.error_message?.match(/\[([^\]]+)\]/)?.[1] || 'unknown';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
  };

  return stats;
}
